import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card } from "../components/ui";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  AreaChart,
  Area,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const TIPO_UTENZA_CONFIG = {
  100000000: { label: "Energia elettrica", color: "#eab308", icon: "⚡" },
  100000001: { label: "Gas naturale", color: "#ea580c", icon: "🔥" },
  100000002: { label: "Acqua", color: "#2563eb", icon: "💧" },
  100000003: { label: "Teleriscaldamento", color: "#dc2626", icon: "🌡️" },
  100000004: { label: "Telecomunicazioni", color: "#9333ea", icon: "📡" },
  100000005: { label: "Rifiuti", color: "#16a34a", icon: "♻️" },
  100000007: { label: "Rifiuti non speciali", color: "#16a34a", icon: "♻️" },
  100000008: { label: "Rifiuti speciali", color: "#b45309", icon: "☣️" },
  100000006: { label: "Altro", color: "#6b7280", icon: "📦" },
};

const MESI_IT = [
  "Gen",
  "Feb",
  "Mar",
  "Apr",
  "Mag",
  "Giu",
  "Lug",
  "Ago",
  "Set",
  "Ott",
  "Nov",
  "Dic",
];

// Color palette for cost breakdown components
const COST_COLORS = {
  importoEnergia: "#3b82f6",
  spesaTrasporto: "#8b5cf6",
  spesaOneri: "#06b6d4",
  quotaFissa: "#64748b",
  quotaPotenza: "#0ea5e9",
  accise: "#f59e0b",
  iva: "#ef4444",
  dispacciamento: "#10b981",
  sbilanciamento: "#84cc16",
};

const COST_LABELS = {
  importoEnergia: "Materia energia/gas",
  spesaTrasporto: "Trasporto",
  spesaOneri: "Oneri di sistema",
  quotaFissa: "Quota fissa",
  quotaPotenza: "Quota potenza",
  accise: "Accise",
  iva: "IVA",
  dispacciamento: "Dispacciamento",
  sbilanciamento: "Sbilanciamento",
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(value);
};

const formatCurrencyShort = (value) => {
  if (value === null || value === undefined) return "-";
  if (value >= 1000) return `€${(value / 1000).toFixed(1)}k`;
  return `€${Math.round(value)}`;
};

const formatMeseLabel = (meseKey) => {
  if (!meseKey) return "";
  const [, m] = meseKey.split("-");
  return MESI_IT[parseInt(m, 10) - 1] || meseKey;
};

function CurrencyTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const sorted = [...payload]
    .filter((e) => e.value > 0)
    .sort((a, b) => b.value - a.value);
  const total = sorted.reduce((sum, e) => sum + (e.value || 0), 0);
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm max-w-xs">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {sorted.map((entry, i) => (
        <p key={i} style={{ color: entry.color || entry.fill }}>
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
      {sorted.length > 1 && (
        <p className="font-semibold text-gray-900 border-t border-gray-200 mt-1 pt-1">
          Totale: {formatCurrency(total)}
        </p>
      )}
    </div>
  );
}

function ConsumptionTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {payload
        .filter((e) => e.value > 0)
        .map((entry, i) => (
          <p key={i} style={{ color: entry.color || entry.stroke }}>
            {entry.name}: {Number(entry.value).toLocaleString("it-IT")}
          </p>
        ))}
    </div>
  );
}

const TAB_PANORAMICA = "panoramica";
const TAB_DETTAGLIO = "dettaglio";

function AnalisiConsumiPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [rsaList, setRsaList] = useState([]);
  const [stats, setStats] = useState(null);
  const [dettaglioCosti, setDettaglioCosti] = useState(null);
  const [dettaglioLoading, setDettaglioLoading] = useState(false);

  const activeTab = searchParams.get("tab") || TAB_PANORAMICA;
  const rsaIdFilter = searchParams.get("rsaId") || "";
  const annoFilter =
    searchParams.get("anno") || new Date().getFullYear().toString();
  const tipoDettaglio = searchParams.get("tipoDettaglio") || "";

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) =>
    (currentYear - i).toString(),
  );

  const handleFilterChange = useCallback(
    (key, value) => {
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  // Fetch panoramica stats
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const rsaData = await apiRequest("/rsa");
        setRsaList(rsaData.value || []);

        const statsParams = new URLSearchParams();
        if (annoFilter) statsParams.append("anno", annoFilter);

        let statsData;
        if (rsaIdFilter) {
          statsData = await apiRequest(
            `/consumi-utenze/stats/rsa/${rsaIdFilter}?${statsParams.toString()}`,
          );
        } else {
          statsData = await apiRequest(
            `/consumi-utenze/stats/overview?${statsParams.toString()}`,
          );
        }
        setStats(statsData);
      } catch (error) {
        console.error("Error fetching analisi data:", error);
        showToast("Errore nel caricamento dei dati", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [apiRequest, rsaIdFilter, annoFilter, showToast]);

  // Fetch dettaglio costi when tab is active
  useEffect(() => {
    if (activeTab !== TAB_DETTAGLIO) return;

    const fetchDettaglio = async () => {
      setDettaglioLoading(true);
      try {
        const params = new URLSearchParams();
        if (annoFilter) params.append("anno", annoFilter);
        if (rsaIdFilter) params.append("rsaId", rsaIdFilter);
        if (tipoDettaglio) params.append("tipoUtenza", tipoDettaglio);

        const data = await apiRequest(
          `/consumi-utenze/stats/dettaglio-costi?${params.toString()}`,
        );
        setDettaglioCosti(data);
      } catch (error) {
        console.error("Error fetching dettaglio costi:", error);
        showToast("Errore nel caricamento dettaglio costi", "error");
      } finally {
        setDettaglioLoading(false);
      }
    };
    fetchDettaglio();
  }, [
    apiRequest,
    activeTab,
    rsaIdFilter,
    annoFilter,
    tipoDettaglio,
    showToast,
  ]);

  // === PANORAMICA DATA ===
  const monthlyStackedData = useMemo(() => {
    if (!stats) return [];
    if (rsaIdFilter) {
      return (stats.andamentoMensile || []).map((m) => ({
        mese: formatMeseLabel(m.mese),
        meseKey: m.mese,
        Totale: Math.round(m.importo * 100) / 100,
      }));
    }
    const perTipo = stats.andamentoMensilePerTipo || [];
    const mesiMap = {};
    for (const item of perTipo) {
      const meseLabel = formatMeseLabel(item.mese);
      if (!mesiMap[item.mese])
        mesiMap[item.mese] = { mese: meseLabel, meseKey: item.mese };
      const cfg = TIPO_UTENZA_CONFIG[item.tipo];
      const key = cfg?.label || "Altro";
      mesiMap[item.mese][key] =
        Math.round(((mesiMap[item.mese][key] || 0) + item.importo) * 100) / 100;
    }
    return Object.values(mesiMap).sort((a, b) =>
      a.meseKey.localeCompare(b.meseKey),
    );
  }, [stats, rsaIdFilter]);

  const activeTipi = useMemo(() => {
    if (rsaIdFilter) return [{ key: "Totale", color: "#3b82f6" }];
    const tipiSet = new Set();
    for (const item of stats?.andamentoMensilePerTipo || []) {
      const cfg = TIPO_UTENZA_CONFIG[item.tipo];
      tipiSet.add(
        JSON.stringify({
          key: cfg?.label || "Altro",
          color: cfg?.color || "#6b7280",
        }),
      );
    }
    return [...tipiSet].map((s) => JSON.parse(s));
  }, [stats, rsaIdFilter]);

  const pieData = useMemo(() => {
    if (!stats) return [];
    return (stats.perTipoUtenza || [])
      .map((t) => {
        const cfg = TIPO_UTENZA_CONFIG[t.tipo];
        return {
          name: cfg?.label || t.label || "Altro",
          value: Math.round((t.importoTotale || t.importo || 0) * 100) / 100,
          color: cfg?.color || "#6b7280",
        };
      })
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [stats]);

  const consumiMensiliData = useMemo(() => {
    if (!stats || rsaIdFilter) return [];
    const perTipo = stats.andamentoMensilePerTipo || [];
    const mesiMap = {};
    for (const item of perTipo) {
      if (!item.consumo) continue;
      const meseLabel = formatMeseLabel(item.mese);
      if (!mesiMap[item.mese])
        mesiMap[item.mese] = { mese: meseLabel, meseKey: item.mese };
      const cfg = TIPO_UTENZA_CONFIG[item.tipo];
      const key = cfg?.label || "Altro";
      mesiMap[item.mese][key] =
        Math.round(((mesiMap[item.mese][key] || 0) + item.consumo) * 100) / 100;
    }
    return Object.values(mesiMap).sort((a, b) =>
      a.meseKey.localeCompare(b.meseKey),
    );
  }, [stats, rsaIdFilter]);

  const consumoTipi = useMemo(() => {
    const tipiSet = new Set();
    for (const item of stats?.andamentoMensilePerTipo || []) {
      if (item.consumo) {
        const cfg = TIPO_UTENZA_CONFIG[item.tipo];
        tipiSet.add(
          JSON.stringify({
            key: cfg?.label || "Altro",
            color: cfg?.color || "#6b7280",
          }),
        );
      }
    }
    return [...tipiSet].map((s) => JSON.parse(s));
  }, [stats]);

  const barComparisonData = useMemo(() => {
    if (!stats) return [];
    return (stats.perTipoUtenza || [])
      .map((t) => {
        const cfg = TIPO_UTENZA_CONFIG[t.tipo];
        return {
          name: cfg?.label || t.label || "Altro",
          importo: Math.round((t.importoTotale || t.importo || 0) * 100) / 100,
          color: cfg?.color || "#6b7280",
        };
      })
      .filter((d) => d.importo > 0)
      .sort((a, b) => b.importo - a.importo);
  }, [stats]);

  // === DETTAGLIO COSTI DATA ===

  // Build separate datasets for Energia Elettrica and Gas
  const dettaglioChartData = useMemo(() => {
    if (!dettaglioCosti) return { luce: [], gas: [], combined: [] };

    const records = dettaglioCosti.andamentoDettaglio || [];
    const luce = [];
    const gas = [];

    // Group by month
    const luceMap = {};
    const gasMap = {};

    for (const r of records) {
      const meseLabel = formatMeseLabel(r.mese);
      if (r.tipo === 100000000) {
        // Energia elettrica
        luceMap[r.mese] = {
          mese: meseLabel,
          meseKey: r.mese,
          count: r.count,
          importoEnergia: r.importoEnergia,
          spesaTrasporto: r.spesaTrasporto,
          spesaOneri: r.spesaOneri,
          quotaFissa: r.quotaFissa,
          quotaPotenza: r.quotaPotenza,
          accise: r.accise,
          iva: r.iva,
          dispacciamento: r.dispacciamento,
          sbilanciamento: r.sbilanciamento,
          consumoTotale: r.consumoTotale,
          consumoF1: r.consumoF1,
          consumoF2: r.consumoF2,
          consumoF3: r.consumoF3,
          importoTotale: r.importoTotale,
        };
      } else if (r.tipo === 100000001) {
        // Gas
        gasMap[r.mese] = {
          mese: meseLabel,
          meseKey: r.mese,
          count: r.count,
          importoEnergia: r.importoEnergia,
          spesaTrasporto: r.spesaTrasporto,
          spesaOneri: r.spesaOneri,
          quotaFissa: r.quotaFissa,
          accise: r.accise,
          iva: r.iva,
          consumoTotale: r.consumoTotale,
          importoTotale: r.importoTotale,
        };
      }
    }

    luce.push(
      ...Object.values(luceMap).sort((a, b) =>
        a.meseKey.localeCompare(b.meseKey),
      ),
    );
    gas.push(
      ...Object.values(gasMap).sort((a, b) =>
        a.meseKey.localeCompare(b.meseKey),
      ),
    );

    return { luce, gas };
  }, [dettaglioCosti]);

  // Determine which cost components have data
  const activeCostKeys = useMemo(() => {
    const checkData = (dataset, keys) =>
      keys.filter((k) => dataset.some((d) => d[k] > 0));

    const luceKeys = checkData(dettaglioChartData.luce, [
      "importoEnergia",
      "spesaTrasporto",
      "spesaOneri",
      "quotaFissa",
      "quotaPotenza",
      "accise",
      "iva",
      "dispacciamento",
      "sbilanciamento",
    ]);
    const gasKeys = checkData(dettaglioChartData.gas, [
      "importoEnergia",
      "spesaTrasporto",
      "spesaOneri",
      "quotaFissa",
      "accise",
      "iva",
    ]);

    return { luce: luceKeys, gas: gasKeys };
  }, [dettaglioChartData]);

  // Pie data for cost composition (aggregate)
  const costCompositionData = useMemo(() => {
    if (!dettaglioCosti) return { luce: [], gas: [] };

    const records = dettaglioCosti.andamentoDettaglio || [];
    const luceTotals = {};
    const gasTotals = {};

    for (const r of records) {
      const target = r.tipo === 100000000 ? luceTotals : gasTotals;
      const keys = [
        "importoEnergia",
        "spesaTrasporto",
        "spesaOneri",
        "quotaFissa",
        "quotaPotenza",
        "accise",
        "iva",
        "dispacciamento",
        "sbilanciamento",
      ];
      for (const k of keys) {
        target[k] = (target[k] || 0) + (r[k] || 0);
      }
    }

    const toPie = (totals) =>
      Object.entries(totals)
        .filter(([, v]) => v > 0.01)
        .map(([k, v]) => ({
          name: COST_LABELS[k] || k,
          value: Math.round(v * 100) / 100,
          color: COST_COLORS[k] || "#6b7280",
        }))
        .sort((a, b) => b.value - a.value);

    return { luce: toPie(luceTotals), gas: toPie(gasTotals) };
  }, [dettaglioCosti]);

  const rsaName = rsaIdFilter
    ? rsaList.find((r) => r.id === rsaIdFilter)?.nome || "RSA"
    : "Tutte le RSA";

  const totaleBollette = stats?.totaleBollette || 0;
  const totaleImporto = stats?.totaleImporto || 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Analisi Consumi e Spese
          </h1>
          <p className="text-gray-500 mt-1">
            {rsaName} - Anno {annoFilter}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/consumi-utenze")}>
          Torna a Lista
        </Button>
      </div>

      {/* Filtri */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anno
            </label>
            <select
              className="w-full border rounded-md px-3 py-2"
              value={annoFilter}
              onChange={(e) => handleFilterChange("anno", e.target.value)}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              RSA
            </label>
            <select
              className="w-full border rounded-md px-3 py-2"
              value={rsaIdFilter}
              onChange={(e) => handleFilterChange("rsaId", e.target.value)}
            >
              <option value="">Tutte le RSA</option>
              {rsaList.map((rsa) => (
                <option key={rsa.id} value={rsa.id}>
                  {rsa.nome}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-500">
              <span className="font-medium text-gray-900 text-lg">
                {totaleBollette}
              </span>{" "}
              bollette -{" "}
              <span className="font-medium text-gray-900">
                {formatCurrency(totaleImporto)}
              </span>{" "}
              totale
            </div>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-0">
          <button
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === TAB_PANORAMICA
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
            onClick={() => handleFilterChange("tab", TAB_PANORAMICA)}
          >
            Panoramica Generale
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === TAB_DETTAGLIO
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
            onClick={() => handleFilterChange("tab", TAB_DETTAGLIO)}
          >
            Dettaglio Voci di Costo
          </button>
        </div>
      </div>

      {/* === TAB PANORAMICA === */}
      {activeTab === TAB_PANORAMICA && (
        <>
          {totaleBollette > 0 ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="p-4 lg:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Andamento Spese Mensili
                  </h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={monthlyStackedData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="mese"
                        tick={{ fontSize: 12 }}
                        stroke="#9ca3af"
                      />
                      <YAxis
                        tickFormatter={formatCurrencyShort}
                        tick={{ fontSize: 12 }}
                        stroke="#9ca3af"
                      />
                      <Tooltip content={<CurrencyTooltip />} />
                      <Legend />
                      {activeTipi.map((t, idx) => (
                        <Bar
                          key={t.key}
                          dataKey={t.key}
                          stackId="spese"
                          fill={t.color}
                          name={t.key}
                          radius={
                            idx === activeTipi.length - 1
                              ? [4, 4, 0, 0]
                              : [0, 0, 0, 0]
                          }
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                <Card className="p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Ripartizione per Tipo
                  </h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend
                        formatter={(value) => (
                          <span className="text-sm text-gray-600">{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {consumiMensiliData.length > 0 && consumoTipi.length > 0 ? (
                  <Card className="p-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      Andamento Consumi Mensili
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={consumiMensiliData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                          dataKey="mese"
                          tick={{ fontSize: 12 }}
                          stroke="#9ca3af"
                        />
                        <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <Tooltip />
                        <Legend />
                        {consumoTipi.map((t) => (
                          <Line
                            key={t.key}
                            type="monotone"
                            dataKey={t.key}
                            stroke={t.color}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            name={t.key}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                ) : null}

                <Card
                  className={`p-4 ${consumiMensiliData.length === 0 || consumoTipi.length === 0 ? "lg:col-span-2" : ""}`}
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Confronto Spese per Tipo Utenza
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={barComparisonData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        type="number"
                        tickFormatter={formatCurrencyShort}
                        tick={{ fontSize: 12 }}
                        stroke="#9ca3af"
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={140}
                        tick={{ fontSize: 12 }}
                        stroke="#9ca3af"
                      />
                      <Tooltip content={<CurrencyTooltip />} />
                      <Bar
                        dataKey="importo"
                        name="Importo"
                        radius={[0, 4, 4, 0]}
                      >
                        {barComparisonData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>

              {/* Tabella riepilogo */}
              <Card className="p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Riepilogo per Tipo Utenza
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-medium text-gray-600">
                          Tipo
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">
                          Bollette
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">
                          Importo Totale
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">
                          Consumo Totale
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">
                          % sul Totale
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(stats?.perTipoUtenza || []).map((t) => {
                        const cfg = TIPO_UTENZA_CONFIG[t.tipo];
                        const importo = t.importoTotale || t.importo || 0;
                        const pct =
                          totaleImporto > 0
                            ? ((importo / totaleImporto) * 100).toFixed(1)
                            : "0.0";
                        return (
                          <tr
                            key={t.tipo}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="py-2 px-3">
                              <span
                                className="inline-block w-3 h-3 rounded-full mr-2"
                                style={{
                                  backgroundColor: cfg?.color || "#6b7280",
                                }}
                              />
                              {cfg?.icon} {cfg?.label || t.label}
                            </td>
                            <td className="text-right py-2 px-3">
                              {t.count || "-"}
                            </td>
                            <td className="text-right py-2 px-3 font-medium">
                              {formatCurrency(importo)}
                            </td>
                            <td className="text-right py-2 px-3">
                              {t.consumoTotale || t.consumo
                                ? `${(t.consumoTotale || t.consumo).toLocaleString("it-IT")} ${t.unita || ""}`
                                : "-"}
                            </td>
                            <td className="text-right py-2 px-3">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 bg-gray-100 rounded-full h-2">
                                  <div
                                    className="h-2 rounded-full"
                                    style={{
                                      width: `${pct}%`,
                                      backgroundColor: cfg?.color || "#6b7280",
                                    }}
                                  />
                                </div>
                                <span className="text-gray-600 w-12 text-right">
                                  {pct}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-300 font-semibold">
                        <td className="py-2 px-3">Totale</td>
                        <td className="text-right py-2 px-3">
                          {totaleBollette}
                        </td>
                        <td className="text-right py-2 px-3">
                          {formatCurrency(totaleImporto)}
                        </td>
                        <td className="text-right py-2 px-3" />
                        <td className="text-right py-2 px-3">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>
            </>
          ) : (
            <Card className="p-12 text-center">
              <p className="text-gray-500 text-lg">
                Nessuna bolletta trovata per {rsaName} nell'anno {annoFilter}
              </p>
              <p className="text-gray-400 mt-2">
                Prova a selezionare un anno diverso o una RSA specifica
              </p>
            </Card>
          )}
        </>
      )}

      {/* === TAB DETTAGLIO VOCI DI COSTO === */}
      {activeTab === TAB_DETTAGLIO && (
        <>
          {/* Filtro tipo utenza */}
          <Card className="p-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">
                Tipo utenza:
              </label>
              <div className="flex gap-2">
                {[
                  { value: "", label: "Tutti (Luce + Gas)" },
                  { value: "100000000", label: "⚡ Energia Elettrica" },
                  { value: "100000001", label: "🔥 Gas Naturale" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      tipoDettaglio === opt.value
                        ? "bg-blue-100 text-blue-700 border border-blue-300"
                        : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                    }`}
                    onClick={() =>
                      handleFilterChange("tipoDettaglio", opt.value)
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          {dettaglioLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : !dettaglioCosti || dettaglioCosti.totalRecords === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-gray-500 text-lg">
                Nessun dato dettaglio costi disponibile
              </p>
              <p className="text-gray-400 mt-2">
                I dati dettaglio vengono estratti automaticamente dalle bollette
                PDF caricate
              </p>
            </Card>
          ) : (
            <>
              {/* ENERGIA ELETTRICA */}
              {(tipoDettaglio === "" || tipoDettaglio === "100000000") &&
                dettaglioChartData.luce.length > 0 && (
                  <DetailSection
                    title="⚡ Energia Elettrica - Composizione Costi"
                    subtitle="Andamento mensile delle voci di costo"
                    data={dettaglioChartData.luce}
                    activeKeys={activeCostKeys.luce}
                    pieData={costCompositionData.luce}
                    showFasce={true}
                  />
                )}

              {/* GAS NATURALE */}
              {(tipoDettaglio === "" || tipoDettaglio === "100000001") &&
                dettaglioChartData.gas.length > 0 && (
                  <DetailSection
                    title="🔥 Gas Naturale - Composizione Costi"
                    subtitle="Andamento mensile delle voci di costo"
                    data={dettaglioChartData.gas}
                    activeKeys={activeCostKeys.gas}
                    pieData={costCompositionData.gas}
                    showFasce={false}
                  />
                )}

              {/* Tabella riepilogativa dettaglio */}
              <DetailSummaryTable
                dettaglioCosti={dettaglioCosti}
                tipoDettaglio={tipoDettaglio}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

/** Sezione grafici dettaglio per singolo tipo utenza */
function DetailSection({
  title,
  subtitle,
  data,
  activeKeys,
  pieData,
  showFasce,
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stacked Area chart - composizione costi mensile */}
        <Card className="p-4 lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Composizione Costi Mensile
          </h3>
          <ResponsiveContainer width="100%" height={360}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mese" tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <YAxis
                tickFormatter={formatCurrencyShort}
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <Tooltip content={<CurrencyTooltip />} />
              <Legend />
              {activeKeys.map((key) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stackId="costs"
                  fill={COST_COLORS[key]}
                  stroke={COST_COLORS[key]}
                  fillOpacity={0.7}
                  name={COST_LABELS[key]}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Pie - composizione totale */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Composizione Totale
          </h3>
          <ResponsiveContainer width="100%" height={360}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
              >
                {pieData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend
                formatter={(value) => (
                  <span className="text-xs text-gray-600">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Riga 2: Importo totale trend + Consumi / Fasce */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Importo totale trend */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Andamento Importo Totale
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mese" tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <YAxis
                tickFormatter={formatCurrencyShort}
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <Tooltip content={<CurrencyTooltip />} />
              <Line
                type="monotone"
                dataKey="importoTotale"
                stroke="#1e40af"
                strokeWidth={2.5}
                dot={{ r: 4, fill: "#1e40af" }}
                name="Importo Totale"
              />
              <Line
                type="monotone"
                dataKey="importoEnergia"
                stroke={COST_COLORS.importoEnergia}
                strokeWidth={1.5}
                strokeDasharray="5 5"
                dot={{ r: 3 }}
                name="Materia energia"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Consumi e Fasce orarie (solo Luce) */}
        {showFasce ? (
          <Card className="p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Consumi per Fascia Oraria (kWh)
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="mese"
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                />
                <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip content={<ConsumptionTooltip />} />
                <Legend />
                <Bar
                  dataKey="consumoF1"
                  stackId="fasce"
                  fill="#2563eb"
                  name="F1 (Punta)"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="consumoF2"
                  stackId="fasce"
                  fill="#7c3aed"
                  name="F2 (Intermedia)"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="consumoF3"
                  stackId="fasce"
                  fill="#06b6d4"
                  name="F3 (Fuori punta)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        ) : (
          <Card className="p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Consumi Gas (Smc)
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="mese"
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                />
                <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip content={<ConsumptionTooltip />} />
                <Bar
                  dataKey="consumoTotale"
                  fill="#ea580c"
                  name="Consumo Smc"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </div>
  );
}

/** Tabella riepilogativa con tutte le voci di costo aggregate */
function DetailSummaryTable({ dettaglioCosti, tipoDettaglio }) {
  const records = dettaglioCosti?.andamentoDettaglio || [];
  if (records.length === 0) return null;

  // Aggregate by tipo
  const byTipo = {};
  for (const r of records) {
    if (!byTipo[r.tipo]) {
      byTipo[r.tipo] = {
        tipo: r.tipo,
        label: r.label,
        count: 0,
        importoTotale: 0,
        importoEnergia: 0,
        spesaTrasporto: 0,
        spesaOneri: 0,
        quotaFissa: 0,
        quotaPotenza: 0,
        accise: 0,
        iva: 0,
        dispacciamento: 0,
        sbilanciamento: 0,
        consumoTotale: 0,
      };
    }
    const t = byTipo[r.tipo];
    t.count += r.count;
    t.importoTotale += r.importoTotale;
    t.importoEnergia += r.importoEnergia;
    t.spesaTrasporto += r.spesaTrasporto;
    t.spesaOneri += r.spesaOneri;
    t.quotaFissa += r.quotaFissa;
    t.quotaPotenza += r.quotaPotenza || 0;
    t.accise += r.accise;
    t.iva += r.iva;
    t.dispacciamento += r.dispacciamento || 0;
    t.sbilanciamento += r.sbilanciamento || 0;
    t.consumoTotale += r.consumoTotale;
  }

  const tipi = Object.values(byTipo);

  const rows = [
    { key: "importoEnergia", label: "Materia energia/gas" },
    { key: "spesaTrasporto", label: "Trasporto e gestione contatore" },
    { key: "spesaOneri", label: "Oneri di sistema" },
    { key: "quotaFissa", label: "Quota fissa" },
    { key: "quotaPotenza", label: "Quota potenza" },
    { key: "dispacciamento", label: "Dispacciamento" },
    { key: "sbilanciamento", label: "Sbilanciamento" },
    { key: "accise", label: "Accise" },
    { key: "iva", label: "IVA" },
  ];

  return (
    <Card className="p-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        Riepilogo Voci di Costo
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-3 font-medium text-gray-600">
                Voce di Costo
              </th>
              {tipi.map((t) => {
                const cfg = TIPO_UTENZA_CONFIG[t.tipo];
                return (
                  <th
                    key={t.tipo}
                    className="text-right py-2 px-3 font-medium text-gray-600"
                  >
                    {cfg?.icon} {cfg?.label}
                  </th>
                );
              })}
              {tipi.length > 1 && (
                <th className="text-right py-2 px-3 font-medium text-gray-600">
                  Totale
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const values = tipi.map((t) => t[row.key] || 0);
              const total = values.reduce((a, b) => a + b, 0);
              if (total < 0.01) return null;
              return (
                <tr
                  key={row.key}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="py-2 px-3 flex items-center gap-2">
                    <span
                      className="inline-block w-3 h-3 rounded-sm"
                      style={{
                        backgroundColor: COST_COLORS[row.key] || "#6b7280",
                      }}
                    />
                    {row.label}
                  </td>
                  {tipi.map((t) => (
                    <td key={t.tipo} className="text-right py-2 px-3">
                      {t[row.key] > 0.01 ? formatCurrency(t[row.key]) : "-"}
                    </td>
                  ))}
                  {tipi.length > 1 && (
                    <td className="text-right py-2 px-3 font-medium">
                      {formatCurrency(total)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300 font-semibold">
              <td className="py-2 px-3">Totale bollette</td>
              {tipi.map((t) => (
                <td key={t.tipo} className="text-right py-2 px-3">
                  {formatCurrency(t.importoTotale)}
                </td>
              ))}
              {tipi.length > 1 && (
                <td className="text-right py-2 px-3">
                  {formatCurrency(
                    tipi.reduce((a, t) => a + t.importoTotale, 0),
                  )}
                </td>
              )}
            </tr>
            <tr className="font-medium text-gray-500">
              <td className="py-2 px-3">N. bollette</td>
              {tipi.map((t) => (
                <td key={t.tipo} className="text-right py-2 px-3">
                  {t.count}
                </td>
              ))}
              {tipi.length > 1 && (
                <td className="text-right py-2 px-3">
                  {tipi.reduce((a, t) => a + t.count, 0)}
                </td>
              )}
            </tr>
            <tr className="font-medium text-gray-500">
              <td className="py-2 px-3">Consumo totale</td>
              {tipi.map((t) => (
                <td key={t.tipo} className="text-right py-2 px-3">
                  {t.consumoTotale > 0
                    ? `${t.consumoTotale.toLocaleString("it-IT")} ${t.tipo === 100000000 ? "kWh" : "Smc"}`
                    : "-"}
                </td>
              ))}
              {tipi.length > 1 && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}

export default AnalisiConsumiPage;
